import {
  IServiceLayoutItem,
  IServiceListView,
  IServiceItem,
  ISectionLayoutItem,
  ServiceEventType,
  IDoc,
  IDiarySchedule,
  IBooking,
  IDiaryService,
  IDiaryScheduleRawTimes,
} from 'shared-types/index';
import { RunningState } from 'shared-types/bookingStatusTypes';
import DateUtilsService from 'external-services/date-utils-service';
import moment from 'moment';
import { orderBy, sortBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { concatMap, filter, map, toArray } from 'rxjs/operators';
import ServiceGroupsService from 'external-services/service-groups-service/serviceGroups.service';
import { FunctionTrackerInfo } from 'shared-types/SharedTypes';
import ScheduleExtensionService from 'external-services/schedule-extension-service';

const NS = 'ServicesLookupService';

class ServicesLookupService {
  public getServiceFromSchedule(
    schedule: IDiarySchedule,
    bookForDate: Date,
    serviceId?: string
  ): IDiaryService | undefined {
    return schedule.services.find((serviceItem) => {
      if (serviceId && serviceId === serviceItem._id) {
        return true;
      }

      if (!serviceId) {
        const serviceStartDate = serviceItem.startTime;
        const serviceEndDate = serviceItem.endTime;
        const isInDateBetween = DateUtilsService.isInBetweenDates(
          bookForDate,
          serviceStartDate,
          serviceEndDate
        );

        if (isInDateBetween) {
          return true;
        }
      }

      return false;
    });
  }

  public getClosestService(schedule: IDiarySchedule, bookForDate: Date): IDiaryService | undefined {
    let closetService: IDiaryService;
    let closetMin: number;

    schedule.services.forEach((serviceItem) => {
      const timeDiffFromStart = DateUtilsService.minsBetweenDates(
        bookForDate,
        serviceItem.startTime
      );
      const timeDiffFromEnd = DateUtilsService.minsBetweenDates(bookForDate, serviceItem.endTime);

      if (!closetService || timeDiffFromStart < closetMin) {
        closetService = serviceItem;
        closetMin = timeDiffFromStart;
      }

      if (timeDiffFromEnd < closetMin) {
        closetService = serviceItem;
        closetMin = timeDiffFromEnd;
      }
    });
    return closetService;
  }

  public getFuntionTrackerInfo(serviceViewList: IServiceListView[]): {
    id: string;
    serviceViewListItem: Omit<IServiceListView, 'service'> & { service: { _id: string } };
  } {
    return {
      id: FunctionTrackerInfo.id,
      serviceViewListItem: {
        name: FunctionTrackerInfo.name,
        serviceIndex: serviceViewList.length,
        service: {
          _id: FunctionTrackerInfo.id,
        },
      },
    };
  }
  public getFuntionTrackerInfoFromListLength(serviceViewListLength: number): {
    id: string;
    serviceViewListItem: Omit<IServiceListView, 'service'> & { service: { _id: string } };
  } {
    return {
      id: FunctionTrackerInfo.id,
      serviceViewListItem: {
        name: FunctionTrackerInfo.name,
        serviceIndex: serviceViewListLength,
        service: {
          _id: FunctionTrackerInfo.id,
        },
      },
    };
  }

  public buildServiceViewList(
    serviceLayouts: IServiceLayoutItem[],
    services: IServiceItem[]
  ): {
    serviceIds: string[];
    serviceViewList: IServiceListView[];
  } {
    const serviceViewList: IServiceListView[] = [];
    const serviceIds = new Set<string>();

    serviceLayouts.forEach((serviceLayout) => {
      if (!serviceIds.has(serviceLayout.serviceId)) {
        serviceIds.add(serviceLayout.serviceId);
        serviceViewList.push({
          name: serviceLayout.name,
          serviceIndex: serviceLayout.serviceIndex,
          service: services[serviceLayout.serviceIndex],
        });
      }
    });
    return {
      serviceIds: Array.from(serviceIds),
      serviceViewList,
    };
  }

  public checkServiceContainsSection(
    service: IServiceItem,
    sectionIdToCheck: string,
    checkForEvent: boolean
  ): {
    serviceFound: boolean;
    serviceWasEvent: boolean;
  } {
    let serviceFound = false;
    let serviceWasEvent = false;
    const sectionLayoutsLength = service.sectionLayouts.length;

    for (let i = sectionLayoutsLength - 1; i >= 0; i--) {
      const myService: ISectionLayoutItem = service.sectionLayouts[i];

      if (!checkForEvent && sectionIdToCheck === myService.sectionId && !myService.sectionNotUsed) {
        serviceFound = true;

        if (
          service.type === 'Event' ||
          service.type === 'event' ||
          (service.eventType && service.eventType === ServiceEventType.PrivateFunction)
        ) {
          serviceWasEvent = true;
          break;
        }
      }
    }

    return {
      serviceFound,
      serviceWasEvent,
    };
  }

  public getNextAvailableService(
    services: IServiceItem[],
    bookingTime: Date
  ): IServiceItem | undefined {
    // gets all services in future and services running at present
    let futureAndRunningServices = services.filter((serviceItem) => {
      const bookingTimeMinutes: number = bookingTime.getHours() * 60 + bookingTime.getMinutes();
      const endTime: Date =
        typeof serviceItem.endTime === 'string'
          ? DateUtilsService.timeToDate(serviceItem.endTime, bookingTime)
          : serviceItem.endTime;
      const serviceEndDateMinutes: number = endTime.getHours() * 60 + endTime.getMinutes();
      return serviceEndDateMinutes >= bookingTimeMinutes;
    });

    // sorts them by start time
    futureAndRunningServices = sortBy(futureAndRunningServices, ['startTime']);

    // uses the earliest service by start time
    let foundService: IServiceItem = futureAndRunningServices[0] || null;

    // if none found, then find the previous service, starting before the bookingTime
    if (!foundService) {
      // MUST BE IN REVERSE
      const reverseOrderServices = [...services].reverse(); // needed a shallow copy because `reverse()` mutates the source, which may be immutable (redux)
      foundService = reverseOrderServices.find((serviceItem) => {
        const bookingTimeMinutes: number = bookingTime.getHours() * 60 + bookingTime.getMinutes();
        const startTime: Date =
          typeof serviceItem.startTime === 'string'
            ? DateUtilsService.timeToDate(serviceItem.startTime, bookingTime)
            : serviceItem.startTime;
        const serviceStartDateMinutes: number = startTime.getHours() * 60 + startTime.getMinutes();
        return bookingTimeMinutes >= serviceStartDateMinutes;
      });
    }

    return foundService;
  }

  public getCurrentServiceState(
    serviceStartTime: Date,
    serviceEndTime: Date,
    timeZoneId: string
  ): RunningState {
    const timeNow: Date = DateUtilsService.getVenueTime(timeZoneId);

    if (moment(timeNow).isSame(serviceStartTime, 'day')) {
      return RunningState.SameDay;
    }

    if (moment(serviceEndTime).isBefore(timeNow)) {
      return RunningState.PastFromYesterday;
    }

    return RunningState.FutureFromTomorrow;
  }

  public getDefaultServicePax(bookingPAX?: number): number {
    const defaultPax = 2;

    if (typeof bookingPAX === 'number') return bookingPAX;

    return defaultPax;
  }

  public getServicesAndServiceGroups(schedule: IDiarySchedule): Observable<IServiceListView[]> {
    const serviceViewList = this.getServiceViewList(schedule);
    const allServiceGroups: Observable<IDoc[]> = ServiceGroupsService.getServiceGroups();

    return allServiceGroups.pipe(
      concatMap((val) => val),
      filter((sg) => {
        const itemFromServiceList = serviceViewList.find((svl) => {
          return sg.ids.includes((svl.service as IServiceItem)?._id);
        });

        if (itemFromServiceList) return true;

        return false;
      }),
      map((sg) => {
        return {
          name: sg.name,
          order: sg.order,
          isListTop: sg.isListTop,
          services: this.getServicesByIds(sg.ids, schedule.services),
          service: null, // null because this is a service group, so it contains 'services' instead
        };
      }),
      toArray(),
      /**
       * Prepends the standard service list. It is important that single services come before
       * service groups in the list, since they use index values within `bookingsByService` and these
       * will not exist for service groups.
       */
      map((val: IServiceListView[]) => serviceViewList.concat(val))
    );
  }
  // remove observer, use simple async
  public async getServicesAndServiceGroupsAsync(
    schedule: IDiarySchedule
  ): Promise<IServiceListView[]> {
    const serviceViewList = this.getServiceViewList(schedule);
    const allServiceGroups: IDoc[] = await ServiceGroupsService.getServiceGroupsAsync();
    const res = allServiceGroups
      .filter(
        (doc) =>
          !!serviceViewList.find((svl) => doc.ids.includes((svl.service as IServiceItem)?._id))
      )
      .map((sg) => {
        return {
          name: sg.name,
          order: sg.order,
          isListTop: sg.isListTop,
          services: this.getServicesByIds(sg.ids, schedule.services),
          service: null, // null because this is a service group, so it contains 'services' instead
        };
      });
    return serviceViewList.concat(res);
  }

  /**
   * Used when switching days while a service is filtered.
   * Checks if the previously filtered service exists on the new date.
   * Returns `currentService` (number) if it does exist, otherwise `0`.
   */
  public getPreviouslyFilteredService(
    previousService: IServiceItem,
    newServiceViewList: IServiceListView[]
  ): number {
    if (!previousService) {
      return 0;
    }

    const newServiceIndex = newServiceViewList.findIndex(
      (item) => item.name === previousService.name
    );

    return newServiceIndex === -1
      ? 0 // resets to 'all' if service not found
      : newServiceIndex;
  }

  /**
   *
   * @param {*} arrayToModify - watch is applied to this array elsewhere is app, so must modify it rather than replace it
   * @param {*} currentService
   * @param {*} serviceViewList
   */
  public getServicesFromServiceViewList(
    currentServiceIndex: number,
    serviceViewList: IServiceListView[]
  ): IServiceItem[] {
    if (!serviceViewList || !serviceViewList.length) {
      return [];
    }

    const currentServiceViewList = serviceViewList[currentServiceIndex];
    return currentServiceViewList.services
      ? currentServiceViewList.services
      : !currentServiceViewList.service
      ? []
      : ([currentServiceViewList.service] as IServiceItem[]);
  }

  /**
   *
   * @param schedule
   * @param tempBooking - only used for Diary. If 'tempBooking.tables' exists, means it was booked via floorplan or timeline, with table specified
   * @param timeZoneId
   * @param originalTime
   * @param chooseFirstInNoMatches
   * @param tableSection
   */
  public selectService(
    schedule: IDiarySchedule | IDiaryScheduleRawTimes,
    tempBooking: Partial<IBooking>,
    timeZoneId: string,
    originalTime: Date,
    chooseFirstInNoMatches: boolean,
    tableSection: string
  ): IDiaryService {
    let hadEvent = false;
    let tempService: IDiaryService;
    if (schedule) {
      let found = false;
      let bookingTime: Date = originalTime;

      if (!bookingTime) {
        bookingTime = DateUtilsService.getVenueTime(timeZoneId);
      }
      const services: IDiaryService[] = ScheduleExtensionService.serviceHasRawDate(schedule)
        ? ScheduleExtensionService.convertServiceTimesToDates(
            (schedule as IDiaryScheduleRawTimes).services,
            timeZoneId
          )
        : (schedule as IDiarySchedule).services;

      //change for loop to foreach here
      services.forEach((service) => {
        let sectionAllowed = true;
        if (tableSection) {
          found = service.sectionLayouts.some(
            (sl) => !sl.sectionClosed && sl.sectionId === tableSection
          );
          if (!found) {
            sectionAllowed = false;
          }
        }

        const checkBookingTime = DateUtilsService.isInBetweenDates(
          bookingTime,
          service.startTime,
          service.endTime
        );

        // check the booking time is in between which service time, then select the service
        if (checkBookingTime && sectionAllowed) {
          /**
           * If 'tempBooking.tables' exists, means it was booked via floorplan or timeline, with table specified
           */
          if (tableSection) {
            const { serviceFound, serviceWasEvent } = this.checkServiceContainsSection(
              service,
              tableSection,
              hadEvent
            );
            hadEvent = serviceWasEvent;
            if (serviceFound) {
              found = true;
              if (tempBooking) {
                tempBooking.serviceId = service._id;
              }
              tempService = service;
            }
          } else {
            /**
             * Otherwise it was booked via the walkin button and no table has yet been specified and we just need to choose the service
             */
            found = true;
            if (tempBooking) {
              tempBooking.serviceId = service._id;
            }
            tempService = service;
          }
        }
      });

      //added a fix
      // if the service is not found they must be in a gap select the next service
      if (!found && bookingTime && !hadEvent) {
        tempService = this.getNextAvailableService(services, bookingTime) as IDiaryService;
      }
      if (chooseFirstInNoMatches && !tempService) {
        tempService = services[0];
      }
    }
    return tempService;
  }

  // ------------------------------------------Private functions-------------------------------

  private getServiceViewList(schedule: IDiarySchedule): IServiceListView[] {
    const serviceViewList: IServiceListView[] = [];
    const serviceIds = new Set();
    const serviceLayoutsOrder = orderBy(schedule.serviceLayouts, 'startTime');

    serviceLayoutsOrder.forEach((serviceLayOutItem) => {
      if (!serviceIds.has(serviceLayOutItem.serviceId)) {
        serviceIds.add(serviceLayOutItem.serviceId);
        serviceViewList.push({
          name: serviceLayOutItem.name,
          order: 0.1,
          services: null,
          service: schedule.services[serviceLayOutItem.serviceIndex],
        });
      }
    });

    return [{ name: 'All', service: null, order: 0 } as IServiceListView].concat(serviceViewList);
  }

  private getServicesByIds(ids: string[], servicesToSearch: IServiceItem[]): IServiceItem[] {
    return ids.reduce((acc, id) => {
      const service = servicesToSearch.find((s) => s._id === id);
      if (service) {
        acc.push(service);
      }
      return acc;
    }, []);
  }
  getLayoutIdsFromServices(
    servicesList: IDiaryService[],
    sectionLayouts: ISectionLayoutItem[],
    bookingTime: Date,
    isFunctionTrackerBooking: boolean
  ) {
    // FT booking will've access to all available layouts
    if (isFunctionTrackerBooking) {
      return this.getAllLayoutIds(bookingTime, servicesList);
    } else {
      return sectionLayouts.map((layout: ISectionLayoutItem) => layout.layoutId);
    }
  }

  getAllLayoutIds(bookingTime: Date, services: IDiaryService[]): string[] {
    const layoutIdList: string[] = [];
    // Iterate through each service
    services?.forEach((service) => {
      if (DateUtilsService.isInBetweenDates(bookingTime, service.startTime, service.endTime)) {
        // Iterate through each layout
        service.sectionLayouts?.forEach((serviceLayout) => {
          // Do not add duplicate layoutId
          if (!layoutIdList.includes(serviceLayout.layoutId)) {
            layoutIdList.push(serviceLayout.layoutId);
          }
        });
      }
    });

    return layoutIdList;
  }

  // sorts sections list, so that isListTop items are at the top (used for section groups)
  getTrueServicesOrder(_items: IServiceListView[]): IServiceListView[] {
    const items: IServiceListView[] = _items.slice();
    let injectionIndex: number = items[0].name === 'All' ? 1 : 0; // skip first index if 'All' exists
    items.forEach((item, i) => {
      if (item.isListTop) {
        // then insert at the top those services with isListTop
        const splicedItem: IServiceListView = items.splice(i, 1)[0];
        items.splice(injectionIndex, 0, splicedItem);
        injectionIndex++;
      }
    });

    return items;
  }
}

const instance = new ServicesLookupService();
export default instance as ServicesLookupService;
